import { styled } from '@mui/material/styles';
import FuseMessage from '@fuse/core/FuseMessage';
import { Fragment, memo, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { useSelectedOrganisation } from '@/contexts/SelectedOrganisationContext';
import type { Layout1ConfigDefaultsType } from '@/components/theme-layouts/layout1/Layout1Config';
import useFuseLayoutSettings from '@fuse/core/FuseLayout/useFuseLayoutSettings';
import FuseSuspense from '@fuse/core/FuseSuspense';
import LeftSideLayout1 from './components/LeftSideLayout1';
import NavbarWrapperLayout1 from './components/NavbarWrapperLayout1';
import RightSideLayout1 from './components/RightSideLayout1';
import FuseDialog from '@fuse/core/FuseDialog';

const Root = styled('div')(({ config }: { config: Layout1ConfigDefaultsType }) => ({
	...(config.mode === 'boxed' && {
		clipPath: 'inset(0)',
		maxWidth: `${config.containerWidth}px`,
		margin: '0 auto',
		boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
	}),
	...(config.mode === 'container' && {
		'& .container': {
			maxWidth: `${config.containerWidth}px`,
			width: '100%',
			margin: '0 auto',
			'@media (min-width: 96rem)': {
				maxWidth: `${config.containerWidth}px!important`,
			},
		},
	}),
	...(config.mode === 'fullwidth' && {
		'& .container': {
			maxWidth: '100%!important',
			width: '100%!important',
		},
	}),
}));

/**
 * The layout 1.
 * Uses React Router Outlet to render child routes
 */
function Layout1() {
	const settings = useFuseLayoutSettings();
	const config = settings.config as Layout1ConfigDefaultsType;
	const { selectedOrganisationId } = useSelectedOrganisation();

	return (
		<Root
			id="fuse-layout"
			config={config}
			className="flex flex-auto w-full"
		>
			{config.leftSidePanel.display && <LeftSideLayout1 />}

			<div className="flex min-w-0 flex-auto">
				{config.navbar.display && config.navbar.position === 'left' && <NavbarWrapperLayout1 />}

				<main
					id="fuse-main"
					className="relative z-10 flex h-full min-w-0 flex-auto flex-col"
				>
					{/* {config.toolbar.display && (
						<ToolbarLayout1 className={config.toolbar.style === 'fixed' ? 'sticky top-0' : ''} />
					)} */}

					<div className="relative z-10 flex h-full flex-auto flex-col">
						<Suspense>
							<FuseDialog />
						</Suspense>
						<FuseSuspense>
							{/* Remounted on an organisation switch, so no page keeps filters or selections from the previous one. */}
							<Fragment key={selectedOrganisationId}>
								<Outlet />
							</Fragment>
						</FuseSuspense>
					</div>
				</main>

				{config.navbar.display && config.navbar.position === 'right' && <NavbarWrapperLayout1 />}
			</div>

			{config.rightSidePanel.display && <RightSideLayout1 />}
			<FuseMessage />
		</Root>
	);
}

export default memo(Layout1);
